/* Saying what just happened, without stealing the cursor.
 *
 * A save that silently succeeds is a save the person does not trust; a save
 * that opens a modal to say so is a save that costs them a click. A toast is
 * the middle, and it is announced to screen readers through `aria-live`.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

type Toast = { id: number; kind: 'ok' | 'error' | 'info'; text: string; detail?: string }
type Push = (kind: Toast['kind'], text: string, detail?: string) => void

const Ctx = createContext<Push>(() => {})
export const useToast = () => useContext(Ctx)

let seq = 0

export function Toaster({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([])

  const push = useCallback<Push>((kind, text, detail) => {
    const id = ++seq
    setItems((current) => [...current, { id, kind, text, detail }])
    // Errors stay longer: a person reading a failure needs more than the two
    // seconds it takes to register that something worked.
    window.setTimeout(() => setItems((c) => c.filter((t) => t.id !== id)),
      kind === 'error' ? 8000 : 3500)
  }, [])

  const value = useMemo(() => push, [push])

  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="toasts" aria-live="polite" aria-atomic="false">
        {items.map((t) => (
          <div key={t.id} className={`toast toast-${t.kind}`} role={t.kind === 'error' ? 'alert' : undefined}>
            <div className="grow">
              <div style={{ fontWeight: 560 }}>{t.text}</div>
              {t.detail ? <div className="small muted">{t.detail}</div> : null}
            </div>
            <button className="btn btn-quiet btn-sm" onClick={() => setItems((c) => c.filter((x) => x.id !== t.id))} aria-label="Dismiss">×</button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  )
}
